import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Container,
  Form,
  Button,
  Card,
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  FaCreditCard,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaAddressCard
} from 'react-icons/fa';
import './Paiement.css';
import { useAuth } from './AuthContext'; // 👈 à ajouter
import { useLanguage } from '../i18n/LanguageContext';
import { clearPaymentDraft, loadPaymentDraft, savePaymentDraft } from '../booking/storage';

const stripePromise = loadStripe('pk_test_51RhDA7JXMTbdcSVGu2KBJwns3Ec2y5vIgnG9y0g2TcH6ST7gUdlAWC5C9K4OvvOah1WtuS8z3KmEXpTyKcqjnobi00OPpyxDTZ');

const PaiementForm = ({ appointmentData, billingInfoInit, servicePriceCents, bookingSummary }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user, loginPatient, signupPatient } = useAuth();
  const { t, language } = useLanguage();

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveCard, setSaveCard] = useState(false);

  const [billingInfo, setBillingInfo] = useState(
    (billingInfoInit && Object.keys(billingInfoInit).length > 0)
      ? billingInfoInit
      : { name: '', email: '', phone: '', address: '' }
  );
  
  const [formErrors, setFormErrors] = useState({});

  const paymentPayload = {
    appointmentData: {
      ...appointmentData,
      patient_id: appointmentData?.patient_id || user?.user_id || undefined,
      email: billingInfo.email || appointmentData?.email || user?.email || '',
      phone: billingInfo.phone || appointmentData?.phone || user?.phone_number || user?.phone || '',
      address: billingInfo.address || appointmentData?.address || user?.address || ''
    },
    billingInfo,
    servicePriceCents,
    bookingSummary
  };

  // 👇 Pré-remplissage auto avec les données de l'utilisateur Cognito
  useEffect(() => {
    const isEmpty = !billingInfoInit || Object.keys(billingInfoInit).length === 0;
    console.log("👤 user:", user);
    if (user && isEmpty) {
      setBillingInfo({
        name: `${user.given_name || user.first_name || ''} ${user.family_name || user.last_name || ''}`.trim(),
        email: user.email || '',
        phone: user.phone_number || user.phone || '',
        address: user.address || ''
      });
    }
  }, [user, billingInfoInit]);
  
  

  const validateForm = () => {
    const errors = {};
    if (!billingInfo.name.trim()) errors.name = t('payment.nameRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingInfo.email)) errors.email = t('payment.invalidEmail');
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(billingInfo.phone.replace(/\D/g, ''))) errors.phone = t('payment.invalidPhone');
    if (!billingInfo.address.trim()) errors.address = t('payment.addressRequired');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePayment = async () => {
    const cardElement = elements.getElement(CardElement);
    if (!stripe || !elements) return;

    const resolvedAppointmentData = paymentPayload.appointmentData;
    const isGuest = !resolvedAppointmentData?.patient_id;

    if (isGuest) {
      const { first_name, last_name, email, phone, address } = resolvedAppointmentData;
      if (!first_name || !last_name || !email || !phone || !address) {
        return setMessage(`❌ ${t('payment.guestDataMissing')}`);
      }
    }

    if (!validateForm()) return setMessage(`❌ ${t('payment.fixErrors')}`);

    setLoading(true);
    setMessage('');

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingInfo.name,
          email: billingInfo.email,
          phone: billingInfo.phone,
          address: { line1: billingInfo.address }
        }
      });
      if (pmError) throw new Error(pmError.message);

      const res = await axios.post('https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/payments/initiate', {
        appointment_data: resolvedAppointmentData,
        payment_method_id: paymentMethod.id,
        billing_info: billingInfo,
        save_card: saveCard
      });

      const { client_secret, alreadyPaid } = res.data;

      if (alreadyPaid) {
        setMessage(`✅ ${t('payment.alreadyPaid')}`);
        clearPaymentDraft();
        return setTimeout(() => navigate('/confirmation-final', {
          state: { appointmentData: resolvedAppointmentData, billingInfo, bookingSummary }
        }), 1500);
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: paymentMethod.id,
        setup_future_usage: saveCard ? 'off_session' : undefined
      });

      if (confirmError) throw new Error(confirmError.message);

      if (paymentIntent.status === 'succeeded') {
        setMessage(`✅ ${t('payment.paymentConfirmed')}`);
        clearPaymentDraft();
        setTimeout(() => navigate('/confirmation-final', {
          state: { appointmentData: resolvedAppointmentData, billingInfo, bookingSummary }
        }), 1500);
      } else {
        setMessage(`⏳ ${t('payment.paymentPending')}`);
      }

    } catch (err) {
      const msg = err?.response?.data?.details || err?.message || t('payment.unknownError');
      setMessage(`❌ ${t('payment.errorPrefix')}${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="paiement-card">
      <Card.Body>
        <h4 className="text-center mb-4">
          {t('payment.title', { amount: (servicePriceCents / 100).toFixed(2) })}
        </h4>

        {!user ? (
          <Alert variant="light" className="mb-4">
            <div className="d-flex flex-column gap-2">
              <strong>{language === 'fr' ? 'Compte optionnel' : 'Optional account'}</strong>
              <span>{language === 'fr' ? 'Vous pouvez continuer jusqu’au paiement sans connexion, ou creer un compte maintenant pour retrouver vos rendez-vous plus facilement.' : 'You can continue to payment without signing in, or create an account now to make future appointment follow-up easier.'}</span>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={() => {
                    savePaymentDraft(paymentPayload);
                    loginPatient({ redirectTo: `${window.location.origin}/paiement` });
                  }}
                >
                  {language === 'fr' ? 'Se connecter' : 'Sign in'}
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    savePaymentDraft(paymentPayload);
                    signupPatient({ redirectTo: `${window.location.origin}/paiement` });
                  }}
                >
                  {language === 'fr' ? 'Creer un compte' : 'Create account'}
                </Button>
              </div>
            </div>
          </Alert>
        ) : null}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label><FaUser /> {t('payment.fullName')}</Form.Label>
            <Form.Control
              type="text"
              value={billingInfo.name}
              onChange={(e) => setBillingInfo({ ...billingInfo, name: e.target.value })}
              isInvalid={!!formErrors.name}
            />
            <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><FaEnvelope /> Email</Form.Label>
            <Form.Control
              type="email"
              value={billingInfo.email}
              onChange={(e) => setBillingInfo({ ...billingInfo, email: e.target.value })}
              isInvalid={!!formErrors.email}
            />
            <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><FaPhone /> {t('booking.phone')}</Form.Label>
            <Form.Control
              type="tel"
              value={billingInfo.phone}
              onChange={(e) => setBillingInfo({ ...billingInfo, phone: e.target.value })}
              isInvalid={!!formErrors.phone}
            />
            <Form.Control.Feedback type="invalid">{formErrors.phone}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><FaAddressCard /> {t('booking.address')}</Form.Label>
            <Form.Control
              type="text"
              value={billingInfo.address}
              onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })}
              isInvalid={!!formErrors.address}
            />
            <Form.Control.Feedback type="invalid">{formErrors.address}</Form.Control.Feedback>
          </Form.Group>

          <Form.Check
            type="checkbox"
            label={t('payment.saveCard')}
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            className="mb-3"
          />

          <h5 className="mt-4"><FaCreditCard /> {t('payment.creditCard')}</h5>
          <div className="card-element-wrapper">
            <CardElement />
          </div>

          <Button
            variant="primary"
            onClick={handlePayment}
            disabled={!stripe || loading}
            className="w-100 mt-4"
          >
            {loading ? <Spinner animation="border" size="sm" /> : t('payment.payNow')}
          </Button>

          {message && (
            <Alert variant={message.startsWith('✅') ? 'success' : 'danger'} className="mt-3">
              {message}
            </Alert>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

const Paiement = () => {
  const location = useLocation();
  const routePayload = location.state;
  const savedPayload = loadPaymentDraft();
  const paymentPayload = routePayload?.appointmentData ? routePayload : savedPayload;
  const appointmentData = paymentPayload?.appointmentData;
  const billingInfo = paymentPayload?.billingInfo;
  const servicePriceCents = paymentPayload?.servicePriceCents;
  const bookingSummary = paymentPayload?.bookingSummary;

  useEffect(() => {
    if (routePayload?.appointmentData && routePayload?.servicePriceCents) {
      savePaymentDraft(routePayload);
    }
  }, [routePayload]);

  if (!appointmentData || !servicePriceCents) {
    return <Navigate to="/patient/dashboard/" replace />;
  }

  return (
    <Container className="py-5 paiement-container">
      <Elements stripe={stripePromise}>
        <PaiementForm
          appointmentData={appointmentData}
          billingInfoInit={billingInfo}
          servicePriceCents={servicePriceCents}
          bookingSummary={bookingSummary}
        />
      </Elements>
    </Container>
  );
};

export default Paiement;
